import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Save, Search, Check, X } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useFabrics, useButtons, useFabricStats } from "@/hooks/useFabrics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lining types
const LINING_TYPES = ["Full", "Half", "Quarter", "Unlined"];

const AdminMTMFabricPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order");

  // Fabric selection state
  const [fabricSelection, setFabricSelection] = useState(null);
  const [liningSelection, setLiningSelection] = useState(null);
  const [showFabricDialog, setShowFabricDialog] = useState(false);
  const [showLiningDialog, setShowLiningDialog] = useState(false);
  const [fabricSearch, setFabricSearch] = useState("");
  const [liningSearch, setLiningSearch] = useState("");
  const [fabricCategory, setFabricCategory] = useState("suits");
  const [fabricSource, setFabricSource] = useState("rc"); // "rc" or "mx"
  const [fabricBook, setFabricBook] = useState("all");
  const [constructionType, setConstructionType] = useState("Half Canvas");

  // Additional lining options
  const [liningType, setLiningType] = useState("Full");
  const [bodyLining, setBodyLining] = useState("");
  const [piping, setPiping] = useState("");
  const [pipingSelection, setPipingSelection] = useState(null);
  const [showPipingDialog, setShowPipingDialog] = useState(false);
  const [pipingSearch, setPipingSearch] = useState("");
  
  // Garment selection state (loaded from localStorage)
  const [includeJacket, setIncludeJacket] = useState(false);
  const [includeTrouser, setIncludeTrouser] = useState(false);
  const [includeVest, setIncludeVest] = useState(false);
  const [includeShirt, setIncludeShirt] = useState(false);
  const [garmentType, setGarmentType] = useState("");
  
  // Button selection - separate for each garment
  const [jacketButtonSelection, setJacketButtonSelection] = useState(null);
  const [vestButtonSelection, setVestButtonSelection] = useState(null);
  const [shirtButtonSelection, setShirtButtonSelection] = useState(null);
  const [overcoatButtonSelection, setOvercoatButtonSelection] = useState(null);
  
  // Option to use same buttons for jacket and vest (default true for 3-piece)
  const [vestMatchesJacket, setVestMatchesJacket] = useState(true);
  
  const [showButtonDialog, setShowButtonDialog] = useState(false);
  const [buttonSearch, setButtonSearch] = useState("");
  const [buttonCategory, setButtonCategory] = useState("suit"); // suit, overcoat, shirt
  const [activeButtonGarment, setActiveButtonGarment] = useState("jacket"); // which garment we're selecting buttons for

  const [saving, setSaving] = useState(false);

  // Load garment selections from localStorage
  useEffect(() => {
    const savedGarments = localStorage.getItem("mtm_garments");
    if (savedGarments) {
      const garments = JSON.parse(savedGarments);
      setIncludeJacket(garments.includeJacket || false);
      setIncludeTrouser(garments.includeTrouser || false);
      setIncludeVest(garments.includeVest || false);
      setIncludeShirt(garments.includeShirt || false);
      setGarmentType(garments.garmentType || "");
    }
    
    // Also check general info for garment type
    const savedGeneral = localStorage.getItem("mtm_general");
    if (savedGeneral) {
      const general = JSON.parse(savedGeneral);
      if (general.garmentType) {
        setGarmentType(general.garmentType);
      }
    }
  }, []);

  // Load saved selections from localStorage on mount
  useEffect(() => {
    const savedFabric = localStorage.getItem("mtm_fabric");
    if (savedFabric) {
      const data = JSON.parse(savedFabric);
      setFabricSelection(data.fabricSelection);
      setLiningSelection(data.liningSelection);
      setLiningType(data.liningType || "Full");
      setBodyLining(data.bodyLining || "");
      setPiping(data.piping || "");
      setPipingSelection(data.pipingSelection || null);
      // Load button selections
      setJacketButtonSelection(data.jacketButtonSelection || data.buttonSelection || null);
      setVestButtonSelection(data.vestButtonSelection || null);
      setShirtButtonSelection(data.shirtButtonSelection || null);
      setOvercoatButtonSelection(data.overcoatButtonSelection || null);
      setVestMatchesJacket(data.vestMatchesJacket !== false); // default to true
      if (data.constructionType) setConstructionType(data.constructionType);
    }
  }, []);

  // Fetch fabric stats once for tab counts
  const fabricStats = useFabricStats();
  const rcCounts = fabricStats?.by_source_category?.RC || {};
  const mxCounts = fabricStats?.by_source_category?.MX || {};
  const totalMx = Object.values(mxCounts).reduce((a, b) => a + b, 0);

  // Books available for the current source (RC or MX)
  const booksForSource = useMemo(() => {
    const list = fabricStats?.books_by_source?.[fabricSource === "mx" ? "MX" : "RC"] || [];
    return list.map((b) => b.book).filter(Boolean).sort();
  }, [fabricStats, fabricSource]);

  // Fetch fabrics via API (paginated, server-side search)
  const { items: filteredFabrics, total: fabricsTotal } = useFabrics({
    source: fabricSource === "mx" ? "MX" : "RC",
    category: fabricSource === "mx" ? undefined : fabricCategory,
    book: fabricBook !== "all" ? fabricBook : undefined,
    search: fabricSearch || undefined,
    limit: 100,
  });

  // Fetch linings via API
  const { items: filteredLinings, total: liningsTotal } = useFabrics({
    source: "RC",
    category: "linings",
    search: liningSearch || undefined,
    limit: 100,
  });

  // Piping linings (same catalog)
  const { items: filteredPipingLinings, total: pipingLiningsTotal } = useFabrics({
    source: "RC",
    category: "linings",
    search: pipingSearch || undefined,
    limit: 100,
  });

  // Buttons via API
  const { items: filteredButtons, total: buttonsTotal } = useButtons({
    button_category: buttonCategory,
    search: buttonSearch || undefined,
    limit: 200,
  });

  const handleSaveAndContinue = () => {
    const fabricData = {
      fabricSelection,
      liningSelection,
      liningType,
      bodyLining,
      piping,
      pipingSelection,
      constructionType,
      jacketButtonSelection,
      vestButtonSelection: vestMatchesJacket ? jacketButtonSelection : vestButtonSelection,
      shirtButtonSelection,
      overcoatButtonSelection,
      vestMatchesJacket
    };
    localStorage.setItem("mtm_fabric", JSON.stringify(fabricData));
    toast.success("Fabric & Lining saved!");
    navigate("/admin/mtm-measurements");
  };

  const handleSave = () => {
    const fabricData = {
      fabricSelection,
      liningSelection,
      liningType,
      bodyLining,
      piping,
      pipingSelection,
      constructionType,
      jacketButtonSelection,
      vestButtonSelection: vestMatchesJacket ? jacketButtonSelection : vestButtonSelection,
      shirtButtonSelection,
      overcoatButtonSelection,
      vestMatchesJacket
    };
    localStorage.setItem("mtm_fabric", JSON.stringify(fabricData));
    toast.success("Fabric & Lining saved!");
  };

  // Handle button selection from dialog
  const handleButtonSelect = (button) => {
    if (activeButtonGarment === "jacket") {
      setJacketButtonSelection(button);
      // If vest matches jacket, update vest too
      if (vestMatchesJacket && includeVest) {
        setVestButtonSelection(button);
      }
    } else if (activeButtonGarment === "vest") {
      setVestButtonSelection(button);
    } else if (activeButtonGarment === "shirt") {
      setShirtButtonSelection(button);
    } else if (activeButtonGarment === "overcoat") {
      setOvercoatButtonSelection(button);
    }
    setShowButtonDialog(false);
    toast.success(`Selected button for ${activeButtonGarment}: ${button.code}`);
  };

  // Open button dialog for a specific garment
  const openButtonDialog = (garment, category) => {
    setActiveButtonGarment(garment);
    setButtonCategory(category);
    setButtonSearch("");
    setShowButtonDialog(true);
  };

  // Get the current button selection for a garment
  const getButtonForGarment = (garment) => {
    switch (garment) {
      case "jacket": return jacketButtonSelection;
      case "vest": return vestMatchesJacket ? jacketButtonSelection : vestButtonSelection;
      case "shirt": return shirtButtonSelection;
      case "overcoat": return overcoatButtonSelection;
      default: return null;
    }
  };

  // Clear button selection for a garment
  const clearButtonSelection = (garment) => {
    switch (garment) {
      case "jacket":
        setJacketButtonSelection(null);
        if (vestMatchesJacket) setVestButtonSelection(null);
        break;
      case "vest":
        setVestButtonSelection(null);
        break;
      case "shirt":
        setShirtButtonSelection(null);
        break;
      case "overcoat":
        setOvercoatButtonSelection(null);
        break;
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-16">
      {/* Step Navigation */}
      <section className="sticky top-16 z-50 bg-background/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="flex items-center gap-1 overflow-x-auto py-2">
            {[
              { id: 1, label: "GENERAL INFO", href: "/admin/mtm-order" },
              { id: 2, label: "FABRIC & LINING", href: "/admin/mtm-fabric", active: true },
              { id: 3, label: "MEASUREMENTS", href: "/admin/mtm-measurements" },
              { id: 4, label: "STYLES", href: "/admin/mtm-styles" },
              { id: 5, label: "SUBMIT", href: "/admin/mtm-submit" },
            ].map((step) => (
              <button
                key={step.id}
                onClick={() => navigate(step.href)}
                className={`
                  relative flex items-center gap-2 px-4 py-2 text-xs font-medium whitespace-nowrap transition-all
                  ${step.active 
                    ? "bg-primary text-primary-foreground" 
                    : step.id < 2 
                      ? "bg-green-600 text-white hover:bg-green-700"
                      : "bg-slate-700 text-white hover:bg-slate-600"
                  }
                `}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${step.id < 2 ? 'bg-white/30' : 'bg-white/20'}`}>
                  {step.id < 2 ? <Check size={12} /> : step.id}
                </span>
                {step.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Header */}
      <section className="py-6 border-b border-white/5">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <Button variant="ghost" onClick={() => navigate("/admin/mtm-order")} className="mb-4">
            <ArrowLeft size={16} className="mr-2" /> Back to MTM Order
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-serif text-2xl tracking-tight">Fabric & Lining</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Select fabric and lining options for your order
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-8">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Fabric Selection */}
            <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
              <div className="bg-orange-600 text-white px-4 py-3 font-medium">FABRIC CHOICE</div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground block mb-2">Fabric</label>
                  <Button
                    variant="outline"
                    className="w-full justify-between h-12 bg-background border-white/10"
                    onClick={() => setShowFabricDialog(true)}
                  >
                    {fabricSelection ? (
                      <div className="text-left">
                        <span className="font-mono text-primary">{fabricSelection.rc_code}</span>
                        <span className="text-muted-foreground ml-2">
                          {fabricSelection.color || fabricSelection.pattern || ""}
                        </span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">Select fabric...</span>
                    )}
                    <Search size={18} className="text-muted-foreground" />
                  </Button>
                </div>

                {fabricSelection && (
                  <div className="bg-muted/30 p-4 rounded-lg text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Book:</span>
                      <span>{fabricSelection.book}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Composition:</span>
                      <span>{fabricSelection.composition || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Pattern:</span>
                      <span>{fabricSelection.pattern || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Color:</span>
                      <span>{fabricSelection.color || "-"}</span>
                    </div>
                    {fabricSelection.price && (
                      <div className="flex justify-between font-medium">
                        <span className="text-muted-foreground">Price:</span>
                        <span className="text-primary">${fabricSelection.price}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Construction Type Selection */}
            <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
              <div className="bg-slate-700 text-white px-4 py-3 font-medium">CONSTRUCTION TYPE</div>
              <div className="p-6 space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-2">RC Construction:</p>
                  <div className="flex flex-wrap gap-2">
                    {["Fused", "Half Canvas", "Full Canvas"].map(type => (
                      <button
                        key={type}
                        data-testid={`mtm-construction-${type.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => setConstructionType(type)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          constructionType === type ? "bg-blue-500 text-white" : "bg-background text-muted-foreground hover:text-white border border-white/10"
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-teal-400 mb-2">CTD Construction:</p>
                  <div className="flex flex-wrap gap-2">
                    {["CTD Fused", "CTD Half Canvas", "CTD High-End Half Canvas", "CTD Full Canvas",
                      "CTD Machine Half Canvas", "CTD 1/3 Handmade Half Canvas",
                      "CTD Machine Full Canvas", "CTD 1/3 Handmade Full Canvas",
                      "CTD 1/2 Handmade Full Canvas", "CTD Full Handmade Full Canvas"].map(type => (
                      <button
                        key={type}
                        data-testid={`mtm-construction-${type.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => setConstructionType(type)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          constructionType === type ? "bg-teal-500 text-white" : "bg-background text-muted-foreground hover:text-white border border-white/10"
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Selected: <span className={`font-medium ${constructionType.startsWith('CTD') ? 'text-teal-400' : 'text-blue-400'}`}>{constructionType}</span>
                </p>
              </div>
            </div>

            {/* Lining Selection */}
            <div className="bg-card border border-white/10 rounded-lg overflow-hidden">
              <div className="bg-slate-700 text-white px-4 py-3 font-medium">LINING</div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground block mb-2">Lining Type</label>
                  <Select value={liningType} onValueChange={setLiningType}>
                    <SelectTrigger className="h-12 bg-background border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LINING_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground block mb-2">Body Lining</label>
                  <Button
                    variant="outline"
                    className="w-full justify-between h-12 bg-background border-white/10"
                    onClick={() => setShowLiningDialog(true)}
                  >
                    {liningSelection ? (
                      <span className="font-mono text-primary">{liningSelection.rc_code}</span>
                    ) : (
                      <span className="text-muted-foreground">Select...</span>
                    )}
                    <Search size={18} className="text-muted-foreground" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground block mb-2">Piping</label>
                    <Button
                      variant="outline"
                      className="w-full justify-between h-12 bg-background border-white/10"
                      onClick={() => setShowPipingDialog(true)}
                    >
                      {pipingSelection ? (
                        <span className="font-mono text-primary">{pipingSelection.rc_code}</span>
                      ) : (
                        <span className="text-muted-foreground">Select from RCMTM Catalog...</span>
                      )}
                      <Search size={18} className="text-muted-foreground" />
                    </Button>
                  </div>
                </div>

                {pipingSelection && (
                  <div className="bg-muted/30 p-4 rounded-lg text-sm space-y-2 border border-primary/30">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Selected Piping:</span>
                      <button 
                        type="button" 
                        onClick={() => { setPipingSelection(null); setPiping(""); }} 
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
                      >
                        <X size={12} /> Clear
                      </button>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Code:</span>
                      <span className="font-mono text-primary">{pipingSelection.rc_code}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Book:</span>
                      <span>{pipingSelection.book}</span>
                    </div>
                    {pipingSelection.is_new && (
                      <div className="flex justify-end">
                        <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs">NEW</span>
                      </div>
                    )}
                  </div>
                )}

                {liningSelection && (
                  <div className="bg-muted/30 p-4 rounded-lg text-sm space-y-2">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Code:</span>
                      <span className="font-mono">{liningSelection.rc_code}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Book:</span>
                      <span>{liningSelection.book}</span>
                    </div>
                    {liningSelection.is_new && (
                      <span className="inline-block bg-green-500/20 text-green-400 px-2 py-0.5 rounded text-xs">NEW</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* BUTTONS Section - Dynamic based on garment selection */}
          <div className="mt-8">
            <div className="bg-card rounded-xl p-6 border border-white/10">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-2 h-8 bg-amber-500 rounded-full" />
                <h2 className="text-lg font-semibold">Buttons</h2>
                <span className="text-xs text-muted-foreground ml-2">
                  (Based on selected garments)
                </span>
              </div>
              
              {/* Show message if no garments selected */}
              {!includeJacket && !includeTrouser && !includeVest && !includeShirt && !garmentType.includes("overcoat") && (
                <div className="p-4 bg-muted/30 rounded-lg text-center text-muted-foreground">
                  <p className="text-sm">No garments selected. Please go back to General Info to select garments.</p>
                  <Button variant="link" onClick={() => navigate("/admin/mtm-order")} className="mt-2 text-primary">
                    Go to General Info
                  </Button>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                {/* Jacket Buttons */}
                {(includeJacket || garmentType.includes("suit") || garmentType.includes("jacket")) && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-sm flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                        Jacket Buttons
                      </h3>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full justify-between h-12 bg-background border-white/10 hover:border-amber-500/50"
                      onClick={() => openButtonDialog("jacket", "suit")}
                    >
                      {jacketButtonSelection ? (
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-amber-400">{jacketButtonSelection.code}</span>
                          <span className="text-muted-foreground">|</span>
                          <span className="text-sm truncate">{jacketButtonSelection.name}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Select jacket button...</span>
                      )}
                      <Search size={16} className="text-muted-foreground" />
                    </Button>
                    {jacketButtonSelection && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Material:</span>
                          <span>{jacketButtonSelection.material}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Price:</span>
                          <span className="text-amber-400">${jacketButtonSelection.price}/set</span>
                        </div>
                        <button 
                          type="button" 
                          onClick={() => clearButtonSelection("jacket")} 
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
                        >
                          <X size={10} /> Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Vest Buttons */}
                {(includeVest || garmentType.includes("3-piece") || garmentType.includes("vest")) && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-sm flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                        Vest Buttons
                      </h3>
                      {(includeJacket || garmentType.includes("suit")) && (
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <input
                            type="checkbox"
                            checked={vestMatchesJacket}
                            onChange={(e) => {
                              setVestMatchesJacket(e.target.checked);
                              if (e.target.checked && jacketButtonSelection) {
                                setVestButtonSelection(jacketButtonSelection);
                              }
                            }}
                            className="rounded border-white/20"
                          />
                          <span className="text-muted-foreground">Match jacket buttons</span>
                        </label>
                      )}
                    </div>
                    {vestMatchesJacket && (includeJacket || garmentType.includes("suit")) ? (
                      <div className="p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg text-xs">
                        <p className="text-muted-foreground">
                          {jacketButtonSelection ? (
                            <>Using jacket buttons: <span className="font-mono text-purple-400">{jacketButtonSelection.code}</span></>
                          ) : (
                            "Will use the same buttons as jacket"
                          )}
                        </p>
                      </div>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          className="w-full justify-between h-12 bg-background border-white/10 hover:border-purple-500/50"
                          onClick={() => openButtonDialog("vest", "suit")}
                        >
                          {vestButtonSelection ? (
                            <span className="flex items-center gap-2">
                              <span className="font-mono text-purple-400">{vestButtonSelection.code}</span>
                              <span className="text-muted-foreground">|</span>
                              <span className="text-sm truncate">{vestButtonSelection.name}</span>
                            </span>
                          ) : (
                            <span className="text-muted-foreground">Select vest button...</span>
                          )}
                          <Search size={16} className="text-muted-foreground" />
                        </Button>
                        {vestButtonSelection && (
                          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 text-xs space-y-1">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Material:</span>
                              <span>{vestButtonSelection.material}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Price:</span>
                              <span className="text-purple-400">${vestButtonSelection.price}/set</span>
                            </div>
                            <button 
                              type="button" 
                              onClick={() => clearButtonSelection("vest")} 
                              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
                            >
                              <X size={10} /> Clear
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Shirt Buttons */}
                {(includeShirt || garmentType.includes("shirt")) && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-sm flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        Shirt Buttons
                      </h3>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full justify-between h-12 bg-background border-white/10 hover:border-green-500/50"
                      onClick={() => openButtonDialog("shirt", "shirt")}
                    >
                      {shirtButtonSelection ? (
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-green-400">{shirtButtonSelection.code}</span>
                          <span className="text-muted-foreground">|</span>
                          <span className="text-sm truncate">{shirtButtonSelection.name}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Select shirt button...</span>
                      )}
                      <Search size={16} className="text-muted-foreground" />
                    </Button>
                    {shirtButtonSelection && (
                      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Material:</span>
                          <span>{shirtButtonSelection.material}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Price:</span>
                          <span className="text-green-400">${shirtButtonSelection.price}/set</span>
                        </div>
                        <button 
                          type="button" 
                          onClick={() => clearButtonSelection("shirt")} 
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
                        >
                          <X size={10} /> Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Overcoat Buttons */}
                {garmentType.includes("overcoat") && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium text-sm flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                        Overcoat Buttons
                      </h3>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full justify-between h-12 bg-background border-white/10 hover:border-orange-500/50"
                      onClick={() => openButtonDialog("overcoat", "overcoat")}
                    >
                      {overcoatButtonSelection ? (
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-orange-400">{overcoatButtonSelection.code}</span>
                          <span className="text-muted-foreground">|</span>
                          <span className="text-sm truncate">{overcoatButtonSelection.name}</span>
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Select overcoat button...</span>
                      )}
                      <Search size={16} className="text-muted-foreground" />
                    </Button>
                    {overcoatButtonSelection && (
                      <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3 text-xs space-y-1">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Material:</span>
                          <span>{overcoatButtonSelection.material}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Price:</span>
                          <span className="text-orange-400">${overcoatButtonSelection.price}/set</span>
                        </div>
                        <button 
                          type="button" 
                          onClick={() => clearButtonSelection("overcoat")} 
                          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
                        >
                          <X size={10} /> Clear
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Note */}
          <div className="mt-6 p-4 bg-muted/30 border border-white/10 rounded-lg">
            <p className="text-sm text-muted-foreground italic">
              If only ordering pants, leave the lining section blank. If only ordering a vest, make a selection for Body Lining only.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex justify-between items-center">
            <Button variant="outline" onClick={() => navigate("/admin/mtm-order")}>
              <ArrowLeft size={16} className="mr-2" /> Back
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleSave}>
                <Save size={16} className="mr-2" /> Save
              </Button>
              <Button onClick={handleSaveAndContinue} className="bg-primary px-8">
                Save & Continue <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Fabric Selection Dialog */}
      <Dialog open={showFabricDialog} onOpenChange={setShowFabricDialog}>
        <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Select Fabric</span>
              {fabricSelection && (
                <Button variant="ghost" size="sm" onClick={() => setFabricSelection(null)}>
                  <X size={14} className="mr-1" /> Clear
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Source Tabs: RC vs MX */}
            <div className="flex gap-2 border-b border-white/10 pb-2">
              <Button
                variant={fabricSource === "rc" ? "default" : "ghost"}
                size="sm"
                onClick={() => { setFabricSource("rc"); setFabricCategory("suits"); setFabricSearch(""); setFabricBook("all"); }}
                className="text-xs"
                data-testid="fabric-source-rc"
              >
                RC Fabrics ({(rcCounts.suits || 0) + (rcCounts.overcoats || 0) + (rcCounts.shirts || 0)})
              </Button>
              <Button
                variant={fabricSource === "mx" ? "default" : "ghost"}
                size="sm"
                onClick={() => { setFabricSource("mx"); setFabricSearch(""); setFabricBook("all"); }}
                className="text-xs"
                data-testid="fabric-source-mx"
              >
                MX Fabrics ({totalMx})
              </Button>
            </div>

            {/* RC Sub-category tabs */}
            {fabricSource === "rc" && (
              <div className="flex gap-2">
                {[
                  { key: "suits", label: "Suits", count: rcCounts.suits || 0 },
                  { key: "overcoats", label: "Overcoats", count: rcCounts.overcoats || 0 },
                  { key: "shirts", label: "Shirts", count: rcCounts.shirts || 0 },
                ].map(cat => (
                  <Button
                    key={cat.key}
                    variant={fabricCategory === cat.key ? "secondary" : "ghost"}
                    size="sm"
                    onClick={() => { setFabricCategory(cat.key); setFabricSearch(""); setFabricBook("all"); }}
                    className="text-xs"
                  >
                    {cat.label} ({cat.count})
                  </Button>
                ))}
              </div>
            )}

            {/* Search + Book filter */}
            <div className="flex gap-2 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
                <Input
                  placeholder="Search by code, color, pattern, composition..."
                  value={fabricSearch}
                  onChange={(e) => setFabricSearch(e.target.value)}
                  className="pl-10 bg-background border-white/10"
                  data-testid="fabric-search-input"
                />
              </div>
              <Select value={fabricBook} onValueChange={setFabricBook}>
                <SelectTrigger className="w-44 bg-background border-white/10" data-testid="fabric-book-filter">
                  <SelectValue placeholder="All Books" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="all">All Books ({fabricSource === "mx" ? totalMx : ((rcCounts[fabricCategory]) || 0)})</SelectItem>
                  {booksForSource.map((b) => (
                    <SelectItem key={b} value={b}>{b}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Results */}
            <div className="max-h-[400px] overflow-y-auto border border-white/10 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-xs">Book</th>
                    <th className="text-left p-2 text-xs">RC Code</th>
                    <th className="text-left p-2 text-xs">Composition</th>
                    <th className="text-left p-2 text-xs">Pattern</th>
                    <th className="text-left p-2 text-xs">Color</th>
                    <th className="text-right p-2 text-xs">Price</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFabrics.map((fabric, idx) => {
                    const code = fabric.rc_code || fabric.code;
                    return (
                    <tr 
                      key={`${code}-${idx}`}
                      className={`border-b border-white/5 hover:bg-muted/30 cursor-pointer ${
                        fabricSelection?.rc_code === code || fabricSelection?.code === code ? "bg-primary/20" : ""
                      }`}
                      onClick={() => {
                        const normalized = { ...fabric, rc_code: code, source: fabricSource };
                        setFabricSelection(normalized);
                        setShowFabricDialog(false);
                        toast.success(`Selected: ${code}`);
                      }}
                    >
                      <td className="p-2 text-xs text-muted-foreground">{fabric.book}</td>
                      <td className="p-2 font-mono text-xs">{code}</td>
                      <td className="p-2 text-xs">{fabric.composition || "-"}</td>
                      <td className="p-2 text-xs">{fabric.pattern || "-"}</td>
                      <td className="p-2 text-xs">{fabric.color || "-"}</td>
                      <td className="p-2 text-xs text-right">{fabric.price ? `$${fabric.price}` : "-"}</td>
                      <td className="p-2">
                        {(fabricSelection?.rc_code === code || fabricSelection?.code === code) && (
                          <Check size={14} className="text-primary" />
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              Showing {filteredFabrics.length} of {fabricsTotal} fabrics
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lining Selection Dialog */}
      <Dialog open={showLiningDialog} onOpenChange={setShowLiningDialog}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Select Lining from RCMTM Catalog</span>
              {liningSelection && (
                <Button variant="ghost" size="sm" onClick={() => setLiningSelection(null)}>
                  <X size={14} className="mr-1" /> Clear
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                placeholder="Search by lining code..."
                value={liningSearch}
                onChange={(e) => setLiningSearch(e.target.value)}
                className="pl-10 bg-background border-white/10"
              />
            </div>

            {/* Results */}
            <div className="max-h-[400px] overflow-y-auto border border-white/10 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-xs">Book</th>
                    <th className="text-left p-2 text-xs">RC Code</th>
                    <th className="text-left p-2 text-xs">New</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLinings.map((lining, idx) => (
                    <tr 
                      key={`${lining.rc_code}-${idx}`}
                      className={`border-b border-white/5 hover:bg-muted/30 cursor-pointer ${
                        liningSelection?.rc_code === lining.rc_code ? "bg-primary/20" : ""
                      }`}
                      onClick={() => {
                        setLiningSelection(lining);
                        setShowLiningDialog(false);
                        toast.success(`Selected: ${lining.rc_code}`);
                      }}
                    >
                      <td className="p-2 text-xs text-muted-foreground">{lining.book}</td>
                      <td className="p-2 font-mono text-xs">{lining.rc_code}</td>
                      <td className="p-2 text-xs">
                        {lining.is_new && (
                          <span className="bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded text-[10px]">NEW</span>
                        )}
                      </td>
                      <td className="p-2">
                        {liningSelection?.rc_code === lining.rc_code && (
                          <Check size={14} className="text-primary" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              Showing {filteredLinings.length} of {liningsTotal} linings
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Piping Selection Dialog (RCMTM Catalog) */}
      <Dialog open={showPipingDialog} onOpenChange={setShowPipingDialog}>
        <DialogContent className="bg-card border-white/10 max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Select Piping from RCMTM Catalog</span>
              {pipingSelection && (
                <Button variant="ghost" size="sm" onClick={() => { setPipingSelection(null); setPiping(""); }}>
                  <X size={14} className="mr-1" /> Clear
                </Button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
              <Input
                placeholder="Search by piping code..."
                value={pipingSearch}
                onChange={(e) => setPipingSearch(e.target.value)}
                className="pl-10 bg-background border-white/10"
              />
            </div>

            {/* Results */}
            <div className="max-h-[400px] overflow-y-auto border border-white/10 rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 text-xs">Book</th>
                    <th className="text-left p-2 text-xs">RC Code</th>
                    <th className="text-left p-2 text-xs">New</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPipingLinings.map((lining, idx) => (
                    <tr
                      key={`${lining.rc_code}-${idx}`}
                      className={`border-b border-white/5 hover:bg-muted/30 cursor-pointer ${
                        pipingSelection?.rc_code === lining.rc_code ? 'bg-primary/20' : ''
                      }`}
                      onClick={() => {
                        setPipingSelection(lining);
                        setPiping(lining.rc_code);
                        setShowPipingDialog(false);
                        toast.success(`Selected piping: ${lining.rc_code}`);
                      }}
                    >
                      <td className="p-2 text-xs text-muted-foreground">{lining.book}</td>
                      <td className="p-2 font-mono text-xs">{lining.rc_code}</td>
                      <td className="p-2 text-xs">
                        {lining.is_new && (
                          <span className="bg-green-500/20 text-green-400 px-1.5 py-0.5 rounded text-[10px]">NEW</span>
                        )}
                      </td>
                      <td className="p-2">
                        {pipingSelection?.rc_code === lining.rc_code && (
                          <Check size={14} className="text-primary" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground">
              Showing {filteredPipingLinings.length} of {pipingLiningsTotal} linings for piping
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Button Dialog */}
      <Dialog open={showButtonDialog} onOpenChange={setShowButtonDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span>Select Button for</span>
              <span className={`px-2 py-1 rounded text-sm ${
                activeButtonGarment === 'jacket' ? 'bg-blue-500/20 text-blue-400' :
                activeButtonGarment === 'vest' ? 'bg-purple-500/20 text-purple-400' :
                activeButtonGarment === 'shirt' ? 'bg-green-500/20 text-green-400' :
                'bg-orange-500/20 text-orange-400'
              }`}>
                {activeButtonGarment.charAt(0).toUpperCase() + activeButtonGarment.slice(1)}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Search and Category Tabs */}
            <div className="flex gap-4">
              <Input
                placeholder={`Search ${buttonCategory} buttons...`}
                value={buttonSearch}
                onChange={(e) => setButtonSearch(e.target.value)}
                className="flex-1"
              />
              <div className="flex gap-1">
                {[
                  { id: "suit", label: "Suit" },
                  { id: "overcoat", label: "Overcoat" },
                  { id: "shirt", label: "Shirt" }
                ].map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => { setButtonCategory(cat.id); setButtonSearch(""); }}
                    className={`px-3 py-1.5 rounded text-xs font-medium transition-all ${
                      buttonCategory === cat.id 
                        ? 'bg-amber-500 text-white' 
                        : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Material filter chips */}
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="text-muted-foreground">Filter by material:</span>
              {["All", "Real Horn", "Corozo", "Mother of Pearl", "Shell", "Metal", "Polyester", "Leather", "Ceramic"].map(mat => (
                <button
                  key={mat}
                  onClick={() => setButtonSearch(mat === "All" ? "" : mat.toLowerCase())}
                  className="px-2 py-1 rounded bg-muted/50 hover:bg-amber-500/20 text-muted-foreground hover:text-amber-400"
                >
                  {mat}
                </button>
              ))}
            </div>
            
            {/* Button Grid */}
            <div className="max-h-[400px] overflow-y-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {filteredButtons.map((button) => (
                  <div
                    key={button.code}
                    onClick={() => handleButtonSelect(button)}
                    className={`p-3 border rounded-lg hover:bg-amber-500/10 cursor-pointer transition-all group ${
                      getButtonForGarment(activeButtonGarment)?.code === button.code 
                        ? 'border-amber-500 bg-amber-500/20' 
                        : 'border-white/10 hover:border-amber-500'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <code className="text-xs font-mono text-amber-400 bg-amber-500/10 px-1 rounded">{button.code}</code>
                      <span className="text-xs text-muted-foreground">${button.price}</span>
                    </div>
                    <h4 className="font-medium text-sm group-hover:text-amber-300 mb-1">{button.name}</h4>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="bg-muted/50 px-1.5 py-0.5 rounded">{button.material}</span>
                      <span>{button.color}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Sizes: {button.sizes}</p>
                    {getButtonForGarment(activeButtonGarment)?.code === button.code && (
                      <div className="mt-2 flex justify-end">
                        <Check size={14} className="text-amber-400" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {filteredButtons.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  No buttons found matching &quot;{buttonSearch}&quot;
                </div>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground">
              Showing {filteredButtons.length} of {buttonsTotal} buttons
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMTMFabricPage;
